import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { href, Link, matchRoute, navigate, useLocation } from '../../app/router';
import { linkClass } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { useSession } from './session';
import { Spinner } from '../../ui';

/**
 * Renders children for signed-in users; sends everyone else to /login?next=<here>. Anonymous guests (share
 * links) only reach pages that allow them (the board); elsewhere they get the guest page.
 */
export function RequireAuth({ children, allowAnonymous = false }: { children: ReactNode; allowAnonymous?: boolean }) {
  const { t } = useTranslation();
  const session = useSession();
  const { path, search } = useLocation();

  useEffect(() => {
    if (session.status === 'signed-out' && matchRoute(path)?.name !== 'login') navigate(href('login', {}, { next: path + search }), { replace: true });
  }, [session.status, path, search]);

  if (session.status !== 'signed-in') {
    return (
      <div className="flex h-dvh items-center justify-center text-fg-muted">
        <Spinner label={t('auth.loading')} />
      </div>
    );
  }
  if (session.user.isAnonymous && !allowAnonymous) return <GuestHome />;
  return <>{children}</>;
}

function GuestHome() {
  const { t } = useTranslation();
  return (
    <AuthLayout title={t('guest.homeTitle')}>
      <p className="font-sans text-fg-muted">{t('guest.homeBody')}</p>
      <Link className={linkClass} to={href('login')}>
        {t('guest.signIn')}
      </Link>
    </AuthLayout>
  );
}
